

  //Total products
// src/api/dashboard.js

import { api, isDemoActive } from "./client";
import { mockDashboard } from "./mock/dashboard";

const RAW_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  ""; // same-origin if empty

const withBase = (path) => {
  const base = RAW_BASE.replace(/\/?$/, "/");      // ensure trailing slash
  const clean = String(path || "").replace(/^\//, "");
  return base + clean;
};

function getAuthToken() {
  if (typeof window === "undefined") return null;
  const keys = ["accessToken", "token", "authToken", "jwt"];
  for (const k of keys) {
    const v = window.localStorage?.getItem(k);
    if (v) return v;
  }
  // fallback to cookie named "token"
  const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Fetch total products.
 * @param {{storeId?: string, warehouseId?: string, signal?: AbortSignal}} params
 * @returns {Promise<{ totalProducts: number }>}
 */
export async function getTotalProducts({ storeId, warehouseId, signal } = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    withBase(`/api/dashboard/total-products?${qs.toString()}`),
    {
      method: "GET",
      // keep include if you also rely on session cookies; otherwise you can omit
      credentials: "include",
      signal,
      headers,
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    // Surface 401s clearly for your UI, if you want to redirect to login etc.
    if (res.status === 401) {
      throw new Error("Unauthorized. Please sign in again.");
    }
    throw new Error(`Failed to fetch total products (${res.status}): ${msg}`);
  }
  return res.json(); // { totalProducts }
}

// here api id for total stock
export async function getTotalStock({ storeId, warehouseId, signal } = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    withBase(`/api/dashboard/total-stock?${qs.toString()}`),
    {
      method: "GET",
      credentials: "include",
      signal,
      headers,
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) {
      throw new Error("Unauthorized. Please sign in again.");
    }
    throw new Error(`Failed to fetch total stock (${res.status}): ${msg}`);
  }
  return res.json(); // { totalStock, details }
}

// Total out of stock 
export async function getTotalOutOfStock({
  storeId,
  warehouseId,
  distinct = true,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);
  qs.set("distinct", String(distinct));

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/total-out-of-stock?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in.");
    throw new Error(`Failed to fetch out-of-stock (${res.status}): ${msg}`);
  }
  return res.json(); // { totalOutOfStock }
}

// Total Sales Invoice

export async function getSalesInvoiceTotals({
  from,
  to,
  status,
  customerId,
  dateField = "createdAt",
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (customerId) qs.set("customerId", customerId);
  if (dateField) qs.set("dateField", dateField);
  if (status) {
    const val = Array.isArray(status) ? status.join(",") : String(status);
    if (val) qs.set("status", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/sales-invoice/totals?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch sales invoice totals (${res.status}): ${msg}`);
  }
  return res.json(); // { totals: { count, totalAmount, totalPaid, totalDue } }
}

// Total supplier
export async function getTotalSuppliers({ from, to, status, q, signal } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (q) qs.set("q", q);
  if (status) {
    const val = Array.isArray(status) ? status.join(",") : String(status);
    if (val) qs.set("status", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/total-suppliers?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch total suppliers (${res.status}): ${msg}`);
  }

  return res.json(); // { totalSuppliers }
}

// Total Customer 
export async function getTotalCustomers({ from, to, status, q, signal } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (q) qs.set("q", q);
  if (status) {
    const val = Array.isArray(status) ? status.join(",") : String(status);
    if (val) qs.set("status", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/total-customers?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch total customers (${res.status}): ${msg}`);
  }

  return res.json(); // { totalCustomers }
}

// Total Orders
export async function getTotalOrders({
  from,
  to,
  status,
  paymentType,
  customerId,
  q,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (customerId) qs.set("customerId", String(customerId));
  if (q) qs.set("q", q);

  if (status) {
    const val = Array.isArray(status) ? status.join(",") : String(status);
    if (val) qs.set("status", val);
  }

  if (paymentType) {
    const val = Array.isArray(paymentType) ? paymentType.join(",") : String(paymentType);
    if (val) qs.set("paymentType", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/total-orders?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch total orders (${res.status}): ${msg}`);
  }

  return res.json(); // { totalOrders }
}

// Total Customers Overview
export async function getCustomersOverview({ period, from, to, signal } = {}) {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/customers/overview?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch customers overview (${res.status}): ${msg}`);
  }
  return res.json(); // { period, totals, percentages, series }
}

// Top Selling Products
export async function getTopSellingProducts({
  storeId,
  warehouseId,
  from,
  to,
  status,
  limit,
  sortBy,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (typeof limit === "number") qs.set("limit", String(limit));
  if (sortBy) qs.set("sortBy", sortBy);
  if (status) {
    const val = Array.isArray(status) ? status.join(",") : String(status);
    if (val) qs.set("status", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/top-selling-products?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch top selling products (${res.status}): ${msg}`);
  }

  return res.json(); // { items: [...] }
}

// Top Low stock product
export async function getLowStock({
  storeId,
  warehouseId,
  q,
  page,
  limit,
  sort,
  lang,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);
  if (q) qs.set("q", q);
  if (typeof page === "number") qs.set("page", String(page));
  if (typeof limit === "number") qs.set("limit", String(limit));
  if (sort) qs.set("sort", sort);
  if (lang) qs.set("lang", lang);

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (lang) headers["Accept-Language"] = lang; // helps backend pick labels

  const res = await fetch(withBase(`/api/dashboard/low-stock?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch low stock list (${res.status}): ${msg}`);
  }
  return res.json(); // { total, page, pages, items }
}

// Recent Sales
/**
 * Fetch recent sales merged from SalesInvoice + SalesOnlineOrder.
 * GET /api/dashboard/recent-sales?period=today|weekly|monthly&from=&to=&limit=&source=invoice,online&lang=
 *
 * @param {{ period?: 'today'|'weekly'|'monthly', from?: string, to?: string, limit?: number, source?: string|string[], lang?: 'en'|'ar', signal?: AbortSignal }} params
 * @returns {Promise<{ items: Array<{
 *   source: 'invoice'|'online',
 *   id: string,
 *   reference: string,
 *   date: string,
 *   amount: number,
 *   status: string,
 *   paymentStatus?: string,
 *   title: string,
 *   category?: string|null,
 *   image?: string|null,
 *   customer?: { id: string, name: string }
 * }> }>}
 */
export async function getRecentSales({
  period,
  from,
  to,
  limit,
  source,
  lang,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (typeof limit === "number") qs.set("limit", String(limit));
  if (source) {
    const val = Array.isArray(source) ? source.join(",") : String(source);
    if (val) qs.set("source", val);
  }
  if (lang) qs.set("lang", lang);

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (lang) headers["Accept-Language"] = lang;

  const res = await fetch(withBase(`/api/dashboard/recent-sales?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch recent sales (${res.status}): ${msg}`);
  }
  return res.json(); // { items: [...] }
}

// sales statics

export async function getSalesStats({ year, source, signal } = {}) {
  const qs = new URLSearchParams();
  if (typeof year === "number") qs.set("year", String(year));
  if (source) {
    const val = Array.isArray(source) ? source.join(",") : String(source);
    if (val) qs.set("source", val);
  }

  const token = getAuthToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(withBase(`/api/dashboard/sales-stats?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch sales stats (${res.status}): ${msg}`);
  }
  return res.json();
}

// Get Recent Transactions

export async function getRecentTransactions({
  type = "sale",
  period = "weekly",
  from,
  to,
  limit = 5,
  lang,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (type) qs.set("type", type);
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (limit) qs.set("limit", String(limit));
  if (lang) qs.set("lang", lang);

  const token = getAuthToken?.();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (lang) headers["Accept-Language"] = lang;

  const res = await fetch(withBase(`/api/dashboard/recent-transactions?${qs}`), {
    method: "GET",
    credentials: "include",
    signal,
    headers,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) throw new Error("Unauthorized. Please sign in again.");
    throw new Error(`Failed to fetch recent transactions (${res.status}): ${msg}`);
  }
  return res.json();
}

// get top customer
export async function getTopCustomers({
  period,
  from,
  to,
  limit = 5,
  source = "all",
  storeId,
  warehouseId,
  lang,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (limit) qs.set("limit", String(limit));
  if (source) qs.set("source", source);
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);

  // language header (defaults to ar if browser language starts with ar)
  const resolvedLang =
    lang ||
    (typeof navigator !== "undefined" &&
    navigator.language &&
    navigator.language.toLowerCase().startsWith("ar")
      ? "ar"
      : "en");

  const token = getAuthToken();
  const headers = {
    Accept: "application/json",
    "Accept-Language": resolvedLang,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    withBase(`/api/dashboard/top-customers?${qs.toString()}`),
    {
      method: "GET",
      credentials: "include",
      signal,
      headers,
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) {
      throw new Error("Unauthorized. Please sign in again.");
    }
    throw new Error(`Failed to fetch top customers (${res.status}): ${msg}`);
  }

  return res.json(); // { items, start, end, count }
}

// Get Top Category

export async function getTopCategories({
  period = "weekly",
  from,
  to,
  limit = 3,
  storeId,
  warehouseId,
  sourceStatus = "Completed",
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (limit != null) qs.set("limit", String(limit));
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);
  if (sourceStatus) qs.set("sourceStatus", sourceStatus);

  const token = getAuthToken();
  const headers = {
    Accept: "application/json",
  };

  // Pass preferred language so API can return localized category names
  try {
    const savedLang =
      (typeof window !== "undefined" &&
        (localStorage.getItem("lang") || localStorage.getItem("i18n_lang"))) ||
      "";
    const browserLang =
      typeof navigator !== "undefined" ? navigator.language : "";
    headers["Accept-Language"] = (savedLang || browserLang || "en").startsWith("ar")
      ? "ar"
      : "en";
  } catch {
    headers["Accept-Language"] = "en";
  }

  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    withBase(`/api/dashboard/top-categories?${qs.toString()}`),
    {
      method: "GET",
      credentials: "include",
      signal,
      headers,
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) {
      throw new Error("Unauthorized. Please sign in again.");
    }
    throw new Error(`Failed to fetch top categories (${res.status}): ${msg}`);
  }

  return res.json(); // { items, totalCategories, totalProducts, start, end, chart }
}

// Get order statistics
export async function getOrderStatistics({
  period = "weekly",
  from,
  to,
  metric = "count",              // "count" | "amount"
  dateField = "createdAt",       // "createdAt" | "updatedAt"
  tz,                             // IANA timezone; defaults to browser tz
  status = "all",                 // "all" or specific order status
  storeId,
  warehouseId,
  signal,
} = {}) {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (metric) qs.set("metric", metric);
  if (dateField) qs.set("dateField", dateField);

  // default timezone from browser if not provided
  let finalTz = tz;
  if (!finalTz) {
    try {
      finalTz =
        (typeof Intl !== "undefined" &&
          Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        "UTC";
    } catch {
      finalTz = "UTC";
    }
  }
  qs.set("tz", finalTz);

  if (status) qs.set("status", status);
  if (storeId) qs.set("storeId", storeId);
  if (warehouseId) qs.set("warehouseId", warehouseId);

  const token = getAuthToken?.(); // keep same auth pattern you use
  const headers = { Accept: "application/json" };

  // Optional: pass preferred language (not required by this endpoint,
  // but consistent with your other calls)
  try {
    const savedLang =
      (typeof window !== "undefined" &&
        (localStorage.getItem("lang") || localStorage.getItem("i18n_lang"))) ||
      "";
    const browserLang =
      typeof navigator !== "undefined" ? navigator.language : "";
    headers["Accept-Language"] = (savedLang || browserLang || "en").startsWith(
      "ar"
    )
      ? "ar"
      : "en";
  } catch {
    headers["Accept-Language"] = "en";
  }

  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    withBase(`/api/dashboard/order-statistics?${qs.toString()}`),
    {
      method: "GET",
      credentials: "include",
      signal,
      headers,
    }
  );

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    if (res.status === 401) {
      throw new Error("Unauthorized. Please sign in again.");
    }
    throw new Error(
      `Failed to fetch order statistics (${res.status}): ${msg}`
    );
  }

  // { start, end, period, dateField, metric, timezone, series, days, hours, totalCells }
  return res.json();
}

export async function fetchDashboard() {
  try {
    const { data } = await api.get("/dashboard/summary"); // adjust path if needed
    return { data, demo: false, error: null };
  } catch (e) {
    // if a 403 turned on demo mode, serve mock data
    if (isDemoActive()) {
      return { data: mockDashboard, demo: true, error: null };
    }
    const msg =
      e?.response?.data?.message ||
      e?.message ||
      "Unable to load dashboard right now.";
    return { data: null, demo: false, error: msg };
  }
}